import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import { getFileKind, isPreviewableInline } from "@lobees/chat-core";
import * as url from "helper/UrlHelper";

const KIND_ICON = {
  pdf: PictureAsPdfIcon,
  spreadsheet: TableChartIcon,
  document: InsertDriveFileIcon,
};

const KIND_COLOR = {
  pdf: "#d32f2f",
  spreadsheet: "#2e7d32",
  document: "#5c6bc0",
};

const boxSx = (size, clickable) => ({
  width: size,
  height: size,
  borderRadius: 1.5,
  overflow: "hidden",
  bgcolor: "background.paper",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
  transition: "transform 0.12s ease, box-shadow 0.12s ease",
  ...(clickable
    ? {
        "&:hover": {
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
          transform: "translateY(-1px)",
        },
      }
    : {}),
});

const captionSx = (size) => ({
  display: "block",
  width: size,
  mt: 0.5,
  textAlign: "center",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

// One thumbnail component for both places a file needs to render: the
// composer's pre-send attachment tray (pass `onRemove`) and a sent/received
// message bubble's attachments (pass `onOpen`, omit `onRemove`). Images get
// an actual image preview; everything else gets a type icon — driven by the
// same file/path shape the upload API already returns, so no extra mapping
// is needed between "about to send" and "already sent" attachments.
//
// `loading` renders a same-size/shape placeholder for a file that's still
// uploading (no `file` object exists yet — the server hasn't returned an id/
// path). The composer swaps each of these for a real thumbnail the moment
// its upload resolves, so the user always sees, per file, "uploading" turn
// into "here's what will be sent" rather than one lump status message.
const AttachmentThumbnail = ({ file, size = 72, onRemove, onOpen, loading = false }) => {
  if (loading) {
    return (
      <Box sx={{ width: size }}>
        <Box sx={boxSx(size)}>
          <CircularProgress size={size * 0.35} />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={captionSx(size)}>
          Uploading…
        </Typography>
      </Box>
    );
  }

  const isImage = isPreviewableInline(file.name);
  const kind = getFileKind(file.name);
  const Icon = KIND_ICON[kind] || InsertDriveFileIcon;
  const fileUrl = url.SERVER_URL + file.path;

  return (
    <Box sx={{ position: "relative", width: size }}>
      <Box onClick={onOpen} sx={{ ...boxSx(size, !!onOpen), cursor: onOpen ? "pointer" : "default" }}>
        {isImage ? (
          <Box
            component="img"
            src={fileUrl}
            alt={file.name}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <Icon sx={{ fontSize: size * 0.42, color: KIND_COLOR[kind] || "text.secondary" }} />
        )}
      </Box>

      <Typography variant="caption" title={file.name} sx={{ ...captionSx(size), opacity: 0.85 }}>
        {file.name}
      </Typography>

      {onRemove ? (
        <IconButton
          size="small"
          onClick={onRemove}
          title="Remove"
          sx={{
            position: "absolute",
            top: -8,
            right: -8,
            width: 20,
            height: 20,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            "&:hover": { bgcolor: "grey.200" },
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      ) : null}
    </Box>
  );
};

export default AttachmentThumbnail;
